/**
 * Wallet Service
 *
 * Plain functions used by SessionManager to interact with the wallet store
 * imperatively. Post wallet-package cutover this module keeps STORE
 * BOOKKEEPING ONLY: device operations (Ledger WebHID) and signing live on
 * the provider-owned `@nullaone/wallet-react` core and are injected from
 * the React layer as `SessionWalletOps` (see `../types`).
 */

import { useBrowserWalletStore } from "@/features/wallet";
import type { SessionWalletType } from "../types";

interface PersistedWallet {
  address: string;
  walletType: SessionWalletType | null;
  walletId: string | null;
  walletName: string | null;
  ledgerPath: string | null;
}

/**
 * Read wallet identity from the rehydrated store (localStorage).
 * Does NOT depend on the IndexedDB initialization completing.
 */
export function getPersistedWallet(): PersistedWallet | null {
  const state = useBrowserWalletStore.getState();
  if (!state.publicKey) return null;

  return {
    address: state.publicKey,
    walletType:
      state.walletType === "hot" || state.walletType === "ledger"
        ? state.walletType
        : null,
    walletId: state.walletId,
    walletName: state.walletName,
    ledgerPath: state.ledgerPath,
  };
}

/**
 * Connect a wallet by address via the injected `selectWallet` (from
 * `useBrowserWallet()`), which performs the storage read, hot unlock or
 * Ledger device connect + address verification, and all store updates.
 * This wrapper reads back the resulting identity for the state machine.
 */
export async function connectWallet(
  address: string,
  selectWallet: (address: string) => Promise<void>,
): Promise<{ type: SessionWalletType; walletId: string }> {
  await selectWallet(address);

  const state = useBrowserWalletStore.getState();
  if (
    (state.walletType !== "hot" && state.walletType !== "ledger") ||
    !state.walletId
  ) {
    throw new Error("Failed to connect wallet");
  }
  return { type: state.walletType, walletId: state.walletId };
}

/**
 * Directly unlock wallet in store.
 * For session restore — the wallet is already identified, just set state.
 * Uses raw `setState` (not `setConnected`) so the package store's
 * `justConnected` signature-trigger flag is NOT raised on restore.
 */
export function unlockWallet(
  address: string,
  walletId: string,
  walletName: string | null,
  walletType: SessionWalletType,
  ledgerPath?: string | null,
): void {
  useBrowserWalletStore.setState({
    status: "unlocked",
    walletType,
    publicKey: address,
    walletId,
    walletName,
    ledgerPath: ledgerPath ?? null,
    error: null,
  });
}

/**
 * Lock the wallet — keeps identity but marks as locked.
 */
export function lockWallet(): void {
  useBrowserWalletStore.getState().setLocked();
}
